import { Actions, createEffect, ofType } from "@ngrx/effects";
import { LearningResourcesService } from "src/app/_services/learning-resources.service";
import {
  AddLearningResources,
  AddLearningResourcesError,
  AddLearningResourcesSuccess,
  GetLearningResourceItems,
  GetLearningResourceItemsError,
  GetLearningResourceItemsSuccess,
  GetLearningResources,
  GetLearningResourcesError,
  GetLearningResourcesSuccess,
  GetModuleTypeSuccess,
  RemoveLearningResources,
  RemoveLearningResourcesError,
  RemoveLearningResourcesSuccess,
  ResetLearningResourcesTables,
  ResetLearningResourcesTablesSuccess,
  UpdateActivityDueDate,
  UpdateActivityDueDateError,
  UpdateActivityDueDateSuccess,
  UpdateLearningResources,
  UpdateLearningResourcesError,
} from "./learning-resource.actions";
import { catchError, map, mergeMap, switchMap } from "rxjs/operators";
import {
  LearningResource,
  LearningResourceItem,
  UpdateLearningResourceDto,
} from "src/app/_models/LearningResource";
import { HttpErrorResponse } from "@angular/common/http";
import { of } from "rxjs";
import { Injectable } from "@angular/core";

@Injectable()
export class LearningResourcesEffects {
  constructor(
    private action$: Actions,
    private learningResourcesService: LearningResourcesService
  ) {}

  public getLearningResourcesEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearningResources),
      switchMap((payload) =>
        this.learningResourcesService
          .getLearningResources$(
            payload.courseId,
            payload.learnerId,
            payload.subCategoryCode,
            payload.isInitialLoad
          )
          .pipe(
            map((result: LearningResource) => {
              return GetLearningResourcesSuccess({ result: result });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(
                GetLearningResourcesError({
                  error: error,
                })
              );
            })
          )
      )
    )
  );

  public updateLearningResourcesTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(UpdateLearningResources),
      switchMap((payload) =>
        this.learningResourcesService
          .updateLearningResources$(payload.params)
          .pipe(
            map(() => {
              return GetLearningResources({
                courseId: payload.params.courseId,
                learnerId: payload.params.learnerId,
                subCategoryCode: payload.params.subCategoryCode,
                isInitialLoad: false,
              });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(UpdateLearningResourcesError({ error: error }));
            })
          )
      )
    )
  );

  public addLearningResourcesTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(AddLearningResources),
      switchMap((payload) =>
        this.learningResourcesService
          .addLearningResources$(payload.addDto)
          .pipe(
            mergeMap((result) => {
              const updateDto: UpdateLearningResourceDto = {
                learnerId: payload.addDto.learnerId,
                courseId: payload.addDto.courseId,
                subCategoryCode: payload.addDto.subCategoryCode,
                filterByLevel: payload.addDto.selectedLevel,
                searchFromList: payload.addDto.searchFromList,
                filterByModuleType: payload.addDto.selectedModuleType,
              };

              return [
                AddLearningResourcesSuccess({ result: result }),
                UpdateLearningResources({
                  params: updateDto,
                }),
              ];
            }),
            catchError((error: HttpErrorResponse) => {
              return of(AddLearningResourcesError({ error: error }));
            })
          )
      )
    )
  );

  public updateActivityDueDateEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(UpdateActivityDueDate),
      switchMap((payload) =>
        this.learningResourcesService
          .updateActivityDueDate$(payload.params)
          .pipe(
            mergeMap((result) => {
              var updateDto: UpdateLearningResourceDto = {
                ...updateDto,
                learnerId: payload.params.learnerId,
                courseId: payload.params.courseId,
                subCategoryCode: payload.params.subCategoryCode,
                filterByLevel: payload.params.selectedLevel,
                searchFromList: payload.params.searchFromList,
                filterByModuleType: payload.params.selectedModuleType,
              };
              return [
                UpdateActivityDueDateSuccess({ result: result }),
                UpdateLearningResources({
                  params: updateDto,
                }),
              ];
            }),
            catchError((error: HttpErrorResponse) => {
              return of(UpdateActivityDueDateError({ error: error }));
            })
          )
      )
    )
  );

  public removeLearningResourcesTableEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(RemoveLearningResources),
      switchMap((payload) =>
        this.learningResourcesService
          .removeLearningResources$(payload.removeDto)
          .pipe(
            mergeMap((result) => {
              var updateDto: UpdateLearningResourceDto = {
                ...updateDto,
                learnerId: payload.removeDto.learnerId,
                courseId: payload.removeDto.courseId,
                subCategoryCode: payload.removeDto.subCategoryCode,
                filterByLevel: payload.removeDto.selectedLevel,
                searchFromList: payload.removeDto.searchFromList,
                filterByModuleType: payload.removeDto.selectedModuleType,
              };

              return [
                RemoveLearningResourcesSuccess({ result: result }),
                UpdateLearningResources({
                  params: updateDto,
                }),
              ];
            }),
            catchError((error: HttpErrorResponse) => {
              return of(RemoveLearningResourcesError({ error: error }));
            })
          )
      )
    )
  );

  public getModuleTypeEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearningResources),
      switchMap((payload) =>
        this.learningResourcesService.getModuleTypes$(payload.courseId).pipe(
          map((moduletypes: string[]) => {
            return GetModuleTypeSuccess({ moduleTypes: moduletypes });
          })
        )
      )
    )
  );

  public getLearningResourceItemsEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearningResourceItems),
      switchMap((payload) =>
        this.learningResourcesService
          .getLearningResourceItemsByObjectIds$(
            payload.learnerId,
            payload.courseId,
            payload.objectIds
          )
          .pipe(
            map((learningResourceItems: LearningResourceItem[]) => {
              return GetLearningResourceItemsSuccess({
                learningResourceItems: learningResourceItems,
              });
            }),
            catchError((error: any) => {
              return of(GetLearningResourceItemsError({ error: error }));
            })
          )
      )
    )
  );

  public resetLearningResources$ = createEffect(() =>
    this.action$.pipe(
      ofType(ResetLearningResourcesTables),
      switchMap(() =>
        this.learningResourcesService.resetLearningResources$().pipe(
          map(() => {
            return ResetLearningResourcesTablesSuccess();
          }),
          catchError((error: any) => {
            return of(GetLearningResourceItemsError({ error: error }));
          })
        )
      )
    )
  );
}
